import { useState, FormEvent, useRef, useEffect, useCallback } from "react";
import { useCart } from "@/hooks/useCart";
import { useAuth } from "@/hooks/useAuth";
import { useOrders } from "@/hooks/useOrders";
import { useRewards } from "@/hooks/useRewards";
import { useCities, useStoreSettings } from "@/hooks/useStoreSettings";
import { supabase } from "@/lib/supabase";
import type { OrderData, DiscountState, PointsDiscountState, RedeemCouponState } from "@/pages/checkout/types";
import PaymentMethodSelector from "@/pages/checkout/components/PaymentMethodSelector";
import TransferReceiptUpload from "@/pages/checkout/components/TransferReceiptUpload";
import PointsRedemption from "@/pages/checkout/components/PointsRedemption";
import type { CartItem } from "@/hooks/useCart";

/* ── Stock issue types ── */
interface StockIssue {
  id: number;
  nameAr: string;
  image: string;
  requested: number;
  available: number; // 0 = نفد تماماً
}

/* ── Stock Warning Modal ── */
function StockWarningModal({
  issues,
  onRemoveItems,
  onClose,
}: {
  issues: StockIssue[];
  onRemoveItems: (ids: number[]) => void;
  onClose: () => void;
}) {
  const outOfStock = issues.filter((i) => i.available === 0);
  const lowStock   = issues.filter((i) => i.available > 0 && i.available < i.requested);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/70 backdrop-blur-sm" dir="rtl">
      <div className="bg-[#111] border border-white/10 rounded-3xl w-full max-w-md overflow-hidden shadow-2xl">

        {/* Header */}
        <div className="bg-red-500/10 border-b border-red-500/20 px-6 py-5 flex items-start gap-4">
          <div className="w-12 h-12 flex items-center justify-center bg-red-500/20 rounded-2xl flex-shrink-0">
            <i className="ri-error-warning-fill text-red-400 text-2xl"></i>
          </div>
          <div>
            <p className="text-white font-black text-base" style={{ fontFamily: "'Cairo', sans-serif" }}>
              تغيّر المخزون أثناء تصفّحك!
            </p>
            <p className="text-gray-400 text-xs mt-1 leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>
              بعض المنتجات في سلتك لم تعد متوفرة بالكمية المطلوبة
            </p>
          </div>
        </div>

        {/* Items */}
        <div className="px-6 py-4 flex flex-col gap-3 max-h-64 overflow-y-auto">
          {outOfStock.map((item) => (
            <div key={item.id} className="flex items-center gap-3 bg-red-500/8 border border-red-500/20 rounded-2xl px-4 py-3">
              <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 bg-[#1a1a1a]">
                <img src={item.image} alt={item.nameAr} className="w-full h-full object-cover object-top" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-xs font-bold truncate" style={{ fontFamily: "'Cairo', sans-serif" }}>{item.nameAr}</p>
                <p className="text-red-400 text-[11px] mt-0.5 font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  <i className="ri-close-circle-fill ml-1"></i>نفد المخزون تماماً
                </p>
              </div>
              <span className="bg-red-500/20 text-red-400 text-[10px] font-black px-2 py-1 rounded-full whitespace-nowrap flex-shrink-0">
                غير متوفر
              </span>
            </div>
          ))}

          {lowStock.map((item) => (
            <div key={item.id} className="flex items-center gap-3 bg-amber-400/8 border border-amber-400/20 rounded-2xl px-4 py-3">
              <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 bg-[#1a1a1a]">
                <img src={item.image} alt={item.nameAr} className="w-full h-full object-cover object-top" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-xs font-bold truncate" style={{ fontFamily: "'Cairo', sans-serif" }}>{item.nameAr}</p>
                <p className="text-amber-400 text-[11px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  طلبت <strong>{item.requested}</strong> — المتوفر فقط <strong>{item.available}</strong>
                </p>
              </div>
              <span className="bg-amber-400/20 text-amber-400 text-[10px] font-black px-2 py-1 rounded-full whitespace-nowrap flex-shrink-0">
                {item.available} قطعة
              </span>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 flex flex-col gap-3">
          {outOfStock.length > 0 && (
            <button
              onClick={() => onRemoveItems(outOfStock.map((i) => i.id))}
              className="w-full flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white font-black text-sm py-3.5 rounded-2xl transition-colors cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Cairo', sans-serif" }}
            >
              <i className="ri-delete-bin-line text-base"></i>
              حذف المنتجات غير المتوفرة والمتابعة
            </button>
          )}
          {lowStock.length > 0 && outOfStock.length === 0 && (
            <button
              onClick={onClose}
              className="w-full flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 text-black font-black text-sm py-3.5 rounded-2xl transition-colors cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Cairo', sans-serif" }}
            >
              <i className="ri-check-line text-base"></i>
              حسناً، فهمت — المتابعة بالكمية المتوفرة
            </button>
          )}
          <button
            onClick={onClose}
            className="w-full flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-gray-300 font-bold text-sm py-3 rounded-2xl transition-colors cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Cairo', sans-serif" }}
          >
            <i className="ri-arrow-right-line text-base"></i>
            العودة لتعديل السلة
          </button>
        </div>
      </div>
    </div>
  );
}

interface CustomerFormProps {
  onSuccess: (data: OrderData) => void;
  discount: DiscountState;
  pointsDiscount: PointsDiscountState;
  onPointsDiscountChange: (state: PointsDiscountState) => void;
  isPlatinum?: boolean;
  platinumDiscountPercent?: number;
  redeemCoupon?: RedeemCouponState;
  hasOutOfStockItems?: boolean;
}

// ── Send WhatsApp notification via Edge Function ──
const sendWhatsAppNotification = async (
  type: "admin" | "customer" | "status_update",
  data: {
    orderRef: string;
    phone: string;
    name: string;
    city?: string;
    address?: string;
    notes?: string;
    items: { nameAr: string; qty: number; price: number }[];
    finalPrice: number;
    paymentLabel: string;
    status?: string;
    discountCode?: string;
    discountPercent?: number;
    discountAmount?: number;
    receiptAttached?: boolean;
    bankName?: string;
    bankAccountNumber?: string;
  }
): Promise<{ autoSent: boolean; whatsappUrl?: string }> => {
  try {
    const { data: res, error } = await supabase.functions.invoke("whatsapp-notify", {
      body: { type, ...data },
    });
    if (error || !res?.success) return { autoSent: false };
    return {
      autoSent: res.autoSent ?? false,
      whatsappUrl: res.adminWhatsAppUrl ?? res.whatsappUrl,
    };
  } catch {
    return { autoSent: false };
  }
};

// ── Build admin WhatsApp fallback URL ──
const buildAdminWhatsAppUrl = (
  adminPhone: string,
  data: {
    orderRef: string;
    name: string;
    phone: string;
    city: string;
    address: string;
    notes: string;
    items: { nameAr: string; qty: number; price: number }[];
    finalPrice: number;
    paymentLabel: string;
    storeName: string;
    discountCode?: string;
    discountPercent?: number;
    discountAmount?: number;
    bankName?: string;
    bankAccountNumber?: string;
    receiptAttached?: boolean;
  }
): string => {
  const sep = "─────────────────";
  const itemsLines = data.items
    .map((i) => `  • ${i.nameAr} × ${i.qty}  ←  ${(i.price * i.qty).toLocaleString()} ر.س`)
    .join("\n");
  const lines = [
    `🔔 *طلب جديد — ${data.storeName}*`,
    sep,
    `🔖 *رقم الطلب:* #${data.orderRef}`,
    `⏰ *الوقت:* ${new Date().toLocaleString("ar-SA")}`,
    sep,
    "👤 *بيانات العميل:*",
    `  • الاسم: ${data.name}`,
    `  • الجوال: ${data.phone}`,
    `  • المدينة: ${data.city}`,
    `  • العنوان: ${data.address}`,
    ...(data.notes ? [`  • ملاحظات: ${data.notes}`] : []),
    sep,
    "📦 *المنتجات:*",
    itemsLines,
    sep,
    `💳 *طريقة الدفع:* ${data.paymentLabel}`,
    ...(data.bankName ? [`🏦 *بيانات التحويل:* ${data.bankName} — ${data.bankAccountNumber ?? ""}`] : []),
    ...(data.discountCode ? [
      `🏷️ *كود الخصم:* ${data.discountCode} (${data.discountPercent}%)`,
      `✂️ *الخصم:* — ${(data.discountAmount ?? 0).toLocaleString()} ر.س`,
    ] : []),
    `💰 *الإجمالي: ${data.finalPrice.toLocaleString()} ر.س*`,
    sep,
    ...(data.receiptAttached
      ? ["📎 *إيصال التحويل:* تم إرفاقه"]
      : data.paymentLabel.includes("بنكي")
      ? ["⚠️ *ملاحظة:* لم يرفق العميل إيصال التحويل"]
      : []),
    "⚡ يرجى التواصل مع العميل لتأكيد الطلب",
  ];
  return `https://wa.me/${adminPhone}?text=${encodeURIComponent(lines.join("\n"))}`;
};

export default function CustomerForm({ onSuccess, discount, pointsDiscount, onPointsDiscountChange, isPlatinum = false, platinumDiscountPercent = 5, redeemCoupon, hasOutOfStockItems = false }: CustomerFormProps) {
  const { items, totalPrice, clearCart, removeItem, updateQty } = useCart();
  const { user, isLoggedIn, updateCustomerAddress } = useAuth();
  const { addOrder } = useOrders();
  const { getRewards } = useRewards();
  const { cities } = useCities();
  const { settings } = useStoreSettings();
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [stockIssues, setStockIssues] = useState<StockIssue[]>([]);

  /* ── Check live stock from Supabase ── */
  const checkStock = useCallback(async (): Promise<StockIssue[]> => {
    const issues: StockIssue[] = [];
    for (const item of items) {
      if (!item.id) continue;
      const { data } = await supabase
        .from("products")
        .select("id, stock")
        .eq("id", item.id)
        .maybeSingle();
      const available = data?.stock ?? 0;
      if (available < item.qty) {
        issues.push({
          id: item.id,
          nameAr: item.nameAr,
          image: item.image,
          requested: item.qty,
          available,
        });
      }
    }
    return issues;
  }, [items]);
  const [availablePoints, setAvailablePoints] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'bank'>(() => {
    if (settings.paymentCashEnabled) return 'cash';
    if (settings.paymentBankEnabled) return 'bank';
    return 'cash';
  });
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptPreviewUrl, setReceiptPreviewUrl] = useState<string | null>(null);
  const [savedCity, setSavedCity] = useState<string>("");
  const nameRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const addressRef = useRef<HTMLTextAreaElement>(null);

  // ── Saved address state ──
  const hasSavedAddress = isLoggedIn && !!user?.city && !!user?.address;
  const [editingAddress, setEditingAddress] = useState(false);

  useEffect(() => {
    if (isLoggedIn && user) {
      if (nameRef.current) nameRef.current.value = user.name;
      if (phoneRef.current) phoneRef.current.value = user.phone;
      if (user.city) setSavedCity(user.city);
      if (user.address && addressRef.current) addressRef.current.value = user.address;
    }
  }, [isLoggedIn, user]);

  useEffect(() => {
    if (isLoggedIn && user?.id) {
      getRewards(user.id).then((r) => setAvailablePoints(r?.points ?? 0));
    }
  }, [isLoggedIn, user, getRewards]);

  const discountAmount = Math.round(totalPrice * (discount.percent / 100));
  const platinumDiscountAmount = isPlatinum ? Math.round(totalPrice * (platinumDiscountPercent / 100)) : 0;
  const pointsDiscountAmount = pointsDiscount.applied ? pointsDiscount.discountAmount : 0;
  const redeemCouponDiscountAmount = redeemCoupon?.status === "valid" ? Math.round(totalPrice * (redeemCoupon.discountPercent / 100)) : 0;
  const finalPrice = Math.max(0, totalPrice - discountAmount - platinumDiscountAmount - pointsDiscountAmount - redeemCouponDiscountAmount);

  const validate = (form: HTMLFormElement): Record<string, string> => {
    const errs: Record<string, string> = {};
    const name = (form.elements.namedItem("name") as HTMLInputElement)?.value.trim();
    const phone = (form.elements.namedItem("phone") as HTMLInputElement)?.value.trim();
    const isEmailOnlyUser = isLoggedIn && user && !user.phone;
    if (!name) errs.name = "يرجى إدخال الاسم";
    if (!isEmailOnlyUser) {
      if (!phone || !/^(05\d{8}|009665\d{8}|\+9665\d{8})$/.test(phone.replace(/\s/g, ""))) {
        errs.phone = "يرجى إدخال رقم جوال سعودي صحيح (05xxxxxxxx)";
      }
    }

    // If using saved address (not editing), skip city/address validation
    const usingSavedAddress = hasSavedAddress && !editingAddress;
    if (!usingSavedAddress) {
      const city = (form.elements.namedItem("city") as HTMLSelectElement)?.value;
      const address = (form.elements.namedItem("address") as HTMLTextAreaElement)?.value.trim();
      if (!city) errs.city = "يرجى اختيار المدينة";
      if (!address || address.length < 10) errs.address = "يرجى إدخال عنوان تفصيلي (10 أحرف على الأقل)";
    }
    return errs;
  };

  const handleReceiptChange = (file: File | null, previewUrl: string | null) => {
    setReceiptFile(file);
    setReceiptPreviewUrl(previewUrl);
  };

  /* ── Handle removing out-of-stock items then re-submit ── */
  const handleRemoveAndContinue = useCallback((ids: number[]) => {
    ids.forEach((id) => removeItem(id));
    setStockIssues([]);
  }, [removeItem]);

  /* ── Handle "continue with available qty" for low-stock ── */
  const handleContinueWithAvailable = useCallback(() => {
    stockIssues.forEach((issue) => {
      if (issue.available > 0) updateQty(issue.id, issue.available);
    });
    setStockIssues([]);
  }, [stockIssues, updateQty]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const errs = validate(form);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});

    // ── Live stock check before proceeding ──
    setSubmitting(true);
    let issues: StockIssue[] = [];
    try {
      issues = await checkStock();
    } catch { /* silent — proceed if check fails */ }

    if (issues.length > 0) {
      setStockIssues(issues);
      setSubmitting(false);
      return;
    }

    setSubmitting(true);

    const name = (form.elements.namedItem("name") as HTMLInputElement).value.trim();
    const phone = (form.elements.namedItem("phone") as HTMLInputElement).value.trim();

    // Determine city & address: use saved if not editing
    const usingSavedAddress = hasSavedAddress && !editingAddress;
    const city = usingSavedAddress
      ? (user?.city ?? "")
      : (form.elements.namedItem("city") as HTMLSelectElement).value;
    const address = usingSavedAddress
      ? (user?.address ?? "")
      : (form.elements.namedItem("address") as HTMLTextAreaElement).value.trim();
    const notes = (form.elements.namedItem("notes") as HTMLTextAreaElement)?.value.trim() || "";

    const orderSummary = items.map((i) => `${i.nameAr} × ${i.qty} (${(i.price * i.qty).toLocaleString()} ر.س)`).join(" | ");
    const orderRef = `EB${Date.now().toString().slice(-6)}`;
    const payLabel = paymentMethod === 'bank' ? 'التحويل البنكي' : 'الدفع عند الاستلام';

    const orderItems = items.map(i => ({ nameAr: i.nameAr, qty: i.qty, price: i.price }));
    const appliedDiscountCode = discount.status === "valid" ? discount.code : (isPlatinum ? "PLATINUM5" : "");
    const appliedDiscountPercent = discount.percent + (isPlatinum ? platinumDiscountPercent : 0);
    const totalDiscountAmount = discountAmount + platinumDiscountAmount + pointsDiscountAmount;

    const body = new URLSearchParams({
      name, phone, city, address, notes,
      payment_method: payLabel,
      order_summary: orderSummary,
      total_price: `${totalPrice.toLocaleString()} ر.س`,
      ...(discount.status === "valid" && {
        discount_code: discount.code,
        discount_amount: `${discountAmount.toLocaleString()} ر.س`,
        final_price: `${finalPrice.toLocaleString()} ر.س`,
      }),
      order_ref: orderRef,
    });

    try {
      await fetch("https://readdy.ai/api/form/d76b7ml4hjod66j2enbg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const adminNotifData = {
        orderRef, phone, name, city, address, notes,
        items: orderItems, finalPrice, paymentLabel: payLabel, status: "pending",
        discountCode: appliedDiscountCode || undefined,
        discountPercent: appliedDiscountPercent || undefined,
        discountAmount: totalDiscountAmount || undefined,
        bankName: paymentMethod === 'bank' ? settings.bankName : undefined,
        bankAccountNumber: paymentMethod === 'bank' ? settings.bankAccountNumber : undefined,
        receiptAttached: paymentMethod === 'bank' && !!receiptFile,
      };

      const { autoSent: adminAutoSent, whatsappUrl: adminFallbackUrl } =
        await sendWhatsAppNotification("admin", adminNotifData);

      if (!adminAutoSent) {
        const fallbackUrl = adminFallbackUrl ?? buildAdminWhatsAppUrl(
          settings.adminWhatsApp,
          { ...adminNotifData, storeName: settings.storeName }
        );
        window.open(fallbackUrl, "_blank", "noopener,noreferrer");

        if (paymentMethod === 'bank' && receiptPreviewUrl) {
          const receiptMsg = encodeURIComponent(
            `📎 إيصال التحويل — طلب #${orderRef}\nالعميل: ${name}\nالمبلغ: ${finalPrice.toLocaleString()} ر.س`
          );
          setTimeout(() => {
            window.open(`https://wa.me/${settings.adminWhatsApp}?text=${receiptMsg}`, "_blank", "noopener,noreferrer");
          }, 1500);
        }
      }

      // Save address to customer profile (always update if logged in and address changed)
      if (isLoggedIn && user && !usingSavedAddress) {
        const identifier = user.phone || user.id;
        if (identifier) {
          updateCustomerAddress(identifier, city, address).catch(() => {});
        }
      }

      await addOrder(phone, {
        orderRef, date: new Date().toISOString(),
        name, phone, city, address, notes,
        paymentLabel: payLabel, items: [...items], totalPrice,
        discountCode: appliedDiscountCode, discountPercent: appliedDiscountPercent,
        discountAmount: totalDiscountAmount, finalPrice, status: "pending",
      }, isLoggedIn && user?.id ? user.id : undefined);

      // ── Deduct stock immediately upon order placement ──
      try {
        for (const item of items) {
          if (!item.id) continue;
          const { data: productRow } = await supabase
            .from('products')
            .select('id, stock')
            .eq('id', item.id)
            .maybeSingle();
          if (!productRow) continue;
          const newStock = Math.max(0, (productRow.stock ?? 0) - (item.qty ?? 1));
          await supabase
            .from('products')
            .update({ stock: newStock })
            .eq('id', item.id);
        }
      } catch { /* silent — stock deduction is best-effort */ }

      if (redeemCoupon?.status === "valid" && redeemCoupon.couponId) {
        try {
          await supabase.from("redeem_coupons").update({ status: "used", used_at: new Date().toISOString() }).eq("id", redeemCoupon.couponId);
        } catch { /* silent */ }
      }

      if (pointsDiscount.applied && isLoggedIn && user?.id && pointsDiscount.pointsToRedeem > 0) {
        try {
          const { data: customerRow } = await supabase
            .from("customers").select("points").eq("id", user.id).maybeSingle();
          const currentPoints = customerRow?.points ?? 0;
          const remainingPoints = Math.max(0, currentPoints - pointsDiscount.pointsToRedeem);
          await supabase.from("customers").update({ points: remainingPoints }).eq("id", user.id);
          await supabase.functions.invoke("whatsapp-notify", {
            body: {
              type: "points_redeemed", phone, name, orderRef,
              pointsRedeemed: pointsDiscount.pointsToRedeem,
              discountAmount: pointsDiscount.discountAmount,
              remainingPoints, finalPrice,
            },
          });
        } catch { /* silent */ }
      }

      await sendWhatsAppNotification("customer", {
        orderRef, phone, name, city, items: orderItems, finalPrice, paymentLabel: payLabel, status: "pending",
      });

      clearCart();
      onSuccess({
        name, phone, city, address, notes,
        paymentLabel: payLabel, orderRef, items, totalPrice,
        discountCode: discount.status === "valid" ? discount.code : "",
        discountPercent: discount.percent, discountAmount, finalPrice, adminAutoSent,
      });
    } catch {
      setSubmitting(false);
    }
  };

  return (
    <>
    {/* Stock Warning Modal */}
    {stockIssues.length > 0 && (
      <StockWarningModal
        issues={stockIssues}
        onRemoveItems={handleRemoveAndContinue}
        onClose={() => {
          const hasLowStock = stockIssues.some((i) => i.available > 0 && i.available < i.requested);
          if (hasLowStock) {
            handleContinueWithAvailable();
          } else {
            setStockIssues([]);
          }
        }}
      />
    )}
    <form
      onSubmit={handleSubmit}
      data-readdy-form
      dir="rtl"
      className="flex flex-col gap-6"
      noValidate
    >
      {/* Logged-in user banner */}
      {isLoggedIn && user && (
        <div className="flex items-center gap-3 bg-amber-400/8 border border-amber-400/25 rounded-2xl px-4 py-3">
          <div className="w-9 h-9 flex items-center justify-center bg-amber-400/20 rounded-full flex-shrink-0">
            <span className="text-amber-400 text-sm font-black" style={{ fontFamily: "'Cairo', sans-serif" }}>
              {user.name.charAt(0)}
            </span>
          </div>
          <div className="flex-1">
            <p className="text-amber-400 text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>
              مرحباً {user.name}! تم ملء بياناتك تلقائياً
            </p>
            <p className="text-amber-400/60 text-[11px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
              رقم الجوال المُتحقق: {user.phone}
            </p>
          </div>
          <div className="w-7 h-7 flex items-center justify-center bg-green-400/15 rounded-full flex-shrink-0">
            <i className="ri-shield-check-line text-green-400 text-sm"></i>
          </div>
        </div>
      )}

      {/* Platinum Discount Banner */}
      {isPlatinum && (
        <div className="flex items-center gap-3 bg-cyan-300/8 border border-cyan-300/30 rounded-2xl px-4 py-3">
          <div className="w-10 h-10 flex items-center justify-center bg-cyan-300/15 rounded-xl flex-shrink-0">
            <i className="ri-vip-diamond-line text-cyan-300 text-lg"></i>
          </div>
          <div className="flex-1">
            <p className="text-cyan-300 text-sm font-black" style={{ fontFamily: "'Cairo', sans-serif" }}>
              مزايا العضو البلاتيني مُفعّلة
            </p>
            <p className="text-cyan-300/70 text-xs mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
              خصم {platinumDiscountPercent}% تلقائي على هذا الطلب — وفّرت {platinumDiscountAmount.toLocaleString()} ر.س
            </p>
          </div>
          <div className="flex-shrink-0 bg-cyan-300/15 border border-cyan-300/30 rounded-full px-2.5 py-1">
            <span className="text-cyan-300 text-xs font-black" style={{ fontFamily: "'Cairo', sans-serif" }}>
              -{platinumDiscountPercent}%
            </span>
          </div>
        </div>
      )}

      {/* Section: Personal Info */}
      <div className="rounded-2xl border border-white/5 bg-[#111] p-5 flex flex-col gap-5">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-8 h-8 flex items-center justify-center bg-amber-400/15 rounded-full">
            <i className="ri-user-3-line text-amber-400 text-sm"></i>
          </div>
          <h3 className="text-white font-bold text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
            البيانات الشخصية
          </h3>
        </div>

        {/* Name */}
        <div className="flex flex-col gap-1.5">
          <label className="text-gray-400 text-xs font-medium" style={{ fontFamily: "'Cairo', sans-serif" }}>
            الاسم الكامل <span className="text-amber-400">*</span>
          </label>
          <div className="relative">
            <input
              ref={nameRef}
              name="name"
              type="text"
              defaultValue={isLoggedIn && user ? user.name : ""}
              placeholder="مثال: محمد أحمد العتيبي"
              className={`w-full bg-[#1a1a1a] border ${errors.name ? "border-red-500/60" : isLoggedIn ? "border-amber-400/30" : "border-white/10"} rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-amber-400/50 transition-colors placeholder-gray-600`}
              style={{ fontFamily: "'Cairo', sans-serif" }}
            />
            {isLoggedIn && (
              <i className="ri-shield-check-line absolute left-3 top-1/2 -translate-y-1/2 text-green-400 text-sm pointer-events-none"></i>
            )}
          </div>
          {errors.name && <p className="text-red-400 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>{errors.name}</p>}
        </div>

        {/* Phone */}
        <div className="flex flex-col gap-1.5">
          <label className="text-gray-400 text-xs font-medium" style={{ fontFamily: "'Cairo', sans-serif" }}>
            رقم الجوال <span className="text-amber-400">*</span>
          </label>
          <div className="relative">
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
              🇸🇦 +966
            </span>
            <input
              ref={phoneRef}
              name="phone"
              type="tel"
              defaultValue={isLoggedIn && user ? user.phone : ""}
              placeholder="05XXXXXXXX"
              readOnly={isLoggedIn}
              className={`w-full bg-[#1a1a1a] border ${errors.phone ? "border-red-500/60" : isLoggedIn ? "border-amber-400/30" : "border-white/10"} rounded-xl pr-28 pl-4 py-3 text-white text-sm outline-none focus:border-amber-400/50 transition-colors placeholder-gray-600 ${isLoggedIn ? "cursor-default" : ""}`}
              style={{ fontFamily: "'Cairo', sans-serif" }}
            />
            {isLoggedIn && (
              <i className="ri-shield-check-line absolute left-3 top-1/2 -translate-y-1/2 text-green-400 text-sm pointer-events-none"></i>
            )}
          </div>
          {isLoggedIn && (
            <p className="text-green-400/70 text-[11px] flex items-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
              <i className="ri-lock-2-line"></i>
              رقم مُتحقق منه — لا يمكن تعديله
            </p>
          )}
          {errors.phone && <p className="text-red-400 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>{errors.phone}</p>}
        </div>
      </div>

      {/* Section: Delivery Info */}
      <div className="rounded-2xl border border-white/5 bg-[#111] p-5 flex flex-col gap-5">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-amber-400/15 rounded-full">
              <i className="ri-map-pin-line text-amber-400 text-sm"></i>
            </div>
            <h3 className="text-white font-bold text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
              عنوان التوصيل
            </h3>
          </div>
          {/* Toggle edit button — only when saved address exists */}
          {hasSavedAddress && (
            <button
              type="button"
              onClick={() => setEditingAddress((v) => !v)}
              className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border transition-all cursor-pointer whitespace-nowrap"
              style={{
                fontFamily: "'Cairo', sans-serif",
                background: editingAddress ? "rgba(251,191,36,0.12)" : "rgba(255,255,255,0.04)",
                borderColor: editingAddress ? "rgba(251,191,36,0.4)" : "rgba(255,255,255,0.1)",
                color: editingAddress ? "rgb(251,191,36)" : "rgb(156,163,175)",
              }}
            >
              <i className={editingAddress ? "ri-close-line" : "ri-edit-line"}></i>
              {editingAddress ? "إلغاء التعديل" : "تعديل العنوان"}
            </button>
          )}
        </div>

        {/* ── Saved Address Card (shown when not editing) ── */}
        {hasSavedAddress && !editingAddress ? (
          <div className="flex items-start gap-3 bg-green-400/6 border border-green-400/20 rounded-xl px-4 py-4">
            <div className="w-9 h-9 flex items-center justify-center bg-green-400/15 rounded-full flex-shrink-0 mt-0.5">
              <i className="ri-home-3-line text-green-400 text-base"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-green-400 text-xs font-bold mb-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                عنوانك المحفوظ
              </p>
              <p className="text-white text-sm font-semibold" style={{ fontFamily: "'Cairo', sans-serif" }}>
                {user?.city}
              </p>
              <p className="text-gray-400 text-xs mt-0.5 leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>
                {user?.address}
              </p>
            </div>
            <div className="w-7 h-7 flex items-center justify-center bg-green-400/15 rounded-full flex-shrink-0">
              <i className="ri-checkbox-circle-line text-green-400 text-sm"></i>
            </div>
          </div>
        ) : (
          <>
            {/* City */}
            <div className="flex flex-col gap-1.5">
              <label className="text-gray-400 text-xs font-medium" style={{ fontFamily: "'Cairo', sans-serif" }}>
                المدينة <span className="text-amber-400">*</span>
              </label>
              <div className="relative">
                <select
                  name="city"
                  value={savedCity}
                  onChange={(e) => setSavedCity(e.target.value)}
                  className={`w-full appearance-none bg-[#1a1a1a] border ${errors.city ? "border-red-500/60" : savedCity && isLoggedIn ? "border-amber-400/30" : "border-white/10"} rounded-xl px-4 py-3 text-sm outline-none focus:border-amber-400/50 transition-colors cursor-pointer`}
                  style={{ fontFamily: "'Cairo', sans-serif", color: savedCity ? "rgb(209 213 219)" : "#4b5563" }}
                >
                  <option value="" disabled style={{ color: "#4b5563" }}>اختر مدينتك</option>
                  {cities.map((c) => (
                    <option key={c} value={c} style={{ background: "#1a1a1a", color: "#fff" }}>{c}</option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"></i>
              </div>
              {errors.city && <p className="text-red-400 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>{errors.city}</p>}
            </div>

            {/* Address */}
            <div className="flex flex-col gap-1.5">
              <label className="text-gray-400 text-xs font-medium" style={{ fontFamily: "'Cairo', sans-serif" }}>
                العنوان التفصيلي <span className="text-amber-400">*</span>
              </label>
              <textarea
                ref={addressRef}
                name="address"
                rows={3}
                placeholder="مثال: حي النزهة، شارع الأمير سلطان، عمارة 12، شقة 3"
                maxLength={300}
                defaultValue={isLoggedIn && user?.address ? user.address : ""}
                className={`w-full bg-[#1a1a1a] border ${errors.address ? "border-red-500/60" : isLoggedIn && user?.address ? "border-amber-400/30" : "border-white/10"} rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-amber-400/50 transition-colors placeholder-gray-600 resize-none`}
                style={{ fontFamily: "'Cairo', sans-serif" }}
              />
              {editingAddress && (
                <p className="text-amber-400/70 text-[11px] flex items-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  <i className="ri-information-line"></i>
                  سيتم حفظ العنوان الجديد في ملفك الشخصي تلقائياً
                </p>
              )}
              {errors.address && <p className="text-red-400 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>{errors.address}</p>}
            </div>
          </>
        )}

        {/* Notes */}
        <div className="flex flex-col gap-1.5">
          <label className="text-gray-400 text-xs font-medium" style={{ fontFamily: "'Cairo', sans-serif" }}>
            ملاحظات إضافية <span className="text-gray-600">(اختياري)</span>
          </label>
          <textarea
            name="notes"
            rows={2}
            placeholder="أي تعليمات خاصة بالتوصيل أو الطلب..."
            maxLength={500}
            className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-amber-400/50 transition-colors placeholder-gray-600 resize-none"
            style={{ fontFamily: "'Cairo', sans-serif" }}
          />
        </div>
      </div>

      {/* Payment Method Selector */}
      {(settings.paymentCashEnabled || settings.paymentBankEnabled) && (
        <PaymentMethodSelector
          selected={paymentMethod}
          onChange={(m) => {
            setPaymentMethod(m);
            if (m !== 'bank') {
              setReceiptFile(null);
              setReceiptPreviewUrl(null);
            }
          }}
          settings={settings}
        />
      )}

      {/* Transfer Receipt Upload */}
      {paymentMethod === 'bank' && (
        <div className="rounded-2xl border border-amber-400/20 bg-[#111] p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 flex items-center justify-center bg-amber-400/15 rounded-full">
              <i className="ri-receipt-line text-amber-400 text-sm"></i>
            </div>
            <div>
              <h3 className="text-white font-bold text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
                إيصال التحويل
              </h3>
              <p className="text-gray-500 text-[11px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                ارفع صورة الإيصال لتسريع تأكيد طلبك
              </p>
            </div>
          </div>
          <TransferReceiptUpload
            onFileChange={handleReceiptChange}
            required={false}
            error={errors.receipt}
          />
          {receiptPreviewUrl && (
            <div className="mt-3 flex items-center gap-2 bg-green-400/8 border border-green-400/20 rounded-xl px-3 py-2">
              <i className="ri-checkbox-circle-line text-green-400 text-sm flex-shrink-0"></i>
              <p className="text-green-400 text-xs font-semibold" style={{ fontFamily: "'Cairo', sans-serif" }}>
                الإيصال جاهز للإرسال مع طلبك
              </p>
            </div>
          )}
        </div>
      )}

      {/* Points Redemption Section */}
      {isLoggedIn && (
        <PointsRedemption
          availablePoints={availablePoints}
          totalPrice={totalPrice}
          discountAmount={discountAmount}
          pointsDiscount={pointsDiscount}
          onPointsDiscountChange={onPointsDiscountChange}
        />
      )}

      {/* Discount applied banner */}
      {discount.status === "valid" && (
        <div className="flex items-center gap-3 bg-green-400/8 border border-green-400/20 rounded-2xl px-4 py-3">
          <div className="w-8 h-8 flex items-center justify-center bg-green-400/15 rounded-full flex-shrink-0">
            <i className="ri-coupon-3-line text-green-400 text-sm"></i>
          </div>
          <div className="flex-1">
            <p className="text-green-400 text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>
              تم تطبيق كود الخصم: {discount.code}
            </p>
            <p className="text-green-400/70 text-[11px]" style={{ fontFamily: "'Cairo', sans-serif" }}>
              وفّرت {discountAmount.toLocaleString()} ر.س — خصم {discount.percent}%
            </p>
          </div>
          <i className="ri-checkbox-circle-line text-green-400 text-lg flex-shrink-0"></i>
        </div>
      )}

      {/* WhatsApp Notification Info */}
      <div className="flex items-center gap-3 bg-[#1a1a1a] border border-white/8 rounded-2xl px-4 py-3">
        <div className="w-8 h-8 flex items-center justify-center bg-green-400/15 rounded-full flex-shrink-0">
          <i className="ri-whatsapp-line text-green-400 text-sm"></i>
        </div>
        <div className="flex-1">
          <p className="text-white text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>
            إشعارات واتساب التلقائية
          </p>
          <p className="text-gray-500 text-[11px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
            {paymentMethod === 'bank'
              ? 'سيتواصل معك فريقنا لتأكيد استلام التحويل وشحن طلبك'
              : 'سيتم إرسال تأكيد الطلب لك عبر واتساب فور إتمام الطلب'}
          </p>
        </div>
      </div>

      {/* Submit */}
      {hasOutOfStockItems ? (
        <div className="w-full flex flex-col items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-2xl py-5 px-4 text-center">
          <div className="flex items-center gap-2 text-red-400 font-black text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
            <i className="ri-close-circle-fill text-lg"></i>
            لا يمكن إتمام الطلب
          </div>
          <p className="text-red-400/70 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>
            يرجى إزالة المنتجات غير المتوفرة من السلة أولاً
          </p>
        </div>
      ) : (
        <button
          type="submit"
          disabled={submitting || items.length === 0}
          className="w-full flex items-center justify-center gap-3 bg-amber-400 hover:bg-amber-300 disabled:opacity-60 disabled:cursor-not-allowed text-black font-black py-5 rounded-2xl text-base transition-all cursor-pointer whitespace-nowrap"
          style={{ fontFamily: "'Cairo', sans-serif" }}
        >
          {submitting ? (
            <>
              <i className="ri-loader-4-line animate-spin text-lg"></i>
              جاري إرسال الطلب...
            </>
          ) : (
            <>
              <i className="ri-shield-check-line text-lg"></i>
              تأكيد الطلب —{" "}
              {(discount.status === "valid" || pointsDiscount.applied || isPlatinum) ? (
                <span className="flex items-center gap-1.5">
                  <span className="line-through opacity-60 font-bold text-sm">{totalPrice.toLocaleString()}</span>
                  <span>{finalPrice.toLocaleString()} ر.س</span>
                </span>
              ) : (
                `${totalPrice.toLocaleString()} ر.س`
              )}
            </>
          )}
        </button>
      )}

      <p className="text-center text-gray-600 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>
        <i className="ri-lock-2-line ml-1 text-green-400"></i>
        بيانات طلبك مشفرة وآمنة تماماً
      </p>
    </form>
    </>
  );
}